import React, { useEffect, useState } from 'react';
import './LangflowHistory.scss';
import { Button, IconButton, ListItem, ListItemText, ListSubheader } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { Divider } from 'antd';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import { useTranslation } from 'react-i18next';
import { DialogModal, useModal } from '@shape-ds/components';
import { formatChatTime } from '../../../langflow.helper';
import { useLangflowChatContext } from '../../../contexts/LangflowChat.context';

interface LangflowHistoryProps {
  setIsHistoryOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpen: (isOpen: boolean) => void;
}

export const LangflowHistory = ({ setIsHistoryOpen, setIsOpen }: LangflowHistoryProps) => {
  const { chats, setSelectedSessionId, deleteChat, setIsHistorySession } = useLangflowChatContext();

  const { t } = useTranslation();

  const [now, setNow] = useState(dayjs());

  const todayChats = chats
    .filter((chat) => chat.lastMessageDate.isSame(now, 'day'))
    .toSorted((a, b) => b.lastMessageDate.diff(a.lastMessageDate));
  const olderChats = chats
    .filter((chat) => !chat.lastMessageDate.isSame(now, 'day'))
    .toSorted((a, b) => b.lastMessageDate.diff(a.lastMessageDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(dayjs());
    }, 60_000);

    return () => clearInterval(interval);
  }, []);

  const { openModal } = useModal();

  const renderChatItem = (chat: (typeof chats)[number]) => {
    const title = `${t('chat:chat_history.session')} ${dayjs(chat.lastMessageDate).format(
      'MMM DD, HH:mm:ss',
    )}`;

    return (
      <>
        <ListItem
          key={chat.sessionId}
          button
          className="langflow-history-list-item"
          onClick={() => {
            setSelectedSessionId(chat.sessionId);
            setIsHistorySession(true);
            setIsHistoryOpen(false);
          }}
        >
          <ListItemText
            primary={title}
            secondary={formatChatTime(chat.lastMessageDate.toDate())}
            className="langflow-history-list-item-text"
          />

          <IconButton
            disableRipple
            onClick={(ev) => {
              ev.stopPropagation();
              openModal(
                <DialogModal
                  message={t('chat:chat_history.delete_info')}
                  title={t('chat:chat_history.delete_title')}
                  onConfirm={() => deleteChat(chat.sessionId)}
                  confirmButtonLabel={t('chat:chat_history.delete_confirm')}
                  cancelButtonLabel={t('chat:chat_history.delete_cancel')}
                />,
              );
            }}
            className="langflow-history-list-item-trash-btn langflow-chat-icon-btn"
          >
            <FontAwesomeIcon icon={faTrashCan} color="#999" />
          </IconButton>
        </ListItem>
        <Divider className="langflow-history-list-divider" />
      </>
    );
  };

  return (
    <div className="langflow-history-container">
      <div className="langflow-history-header">
        <IconButton
          onClick={() => setIsHistoryOpen(false)}
          disableRipple
          className="langflow-chat-icon-btn"
        >
          <ArrowBackIosNewRoundedIcon />
        </IconButton>
        <div className="langflow-history-header-title">{t('chat:chat_history.title')}</div>
        <IconButton disableRipple className="langflow-chat-icon-btn langflow-history-close-btn">
          <CloseIcon
            className="langflow-history-close-icon"
            onClick={() => {
              setIsHistoryOpen(false);
              setIsOpen(false);
            }}
          />
        </IconButton>
      </div>
      <div className="langflow-history-items">
        {todayChats.length > 0 && (
          <>
            <ListSubheader disableSticky className="langflow-history-list-subheader">
              {t('chat:chat_history.today')}
            </ListSubheader>
            <Divider className="langflow-history-list-divider" />
            {todayChats.map(renderChatItem)}
          </>
        )}

        {olderChats.length > 0 && (
          <>
            <ListSubheader disableSticky className="langflow-history-list-subheader">
              {t('chat:chat_history.older')}{' '}
            </ListSubheader>
            <Divider className="langflow-history-list-divider" />
            {olderChats.map(renderChatItem)}
          </>
        )}
      </div>
      <div className="langflow-history-footer">
        <Button
          variant="text"
          startIcon={<ChatOutlinedIcon className="langflow-history-start-chat-icon" />}
          disableRipple
          onClick={() => {
            setSelectedSessionId('');
            setIsHistorySession(false);
            setIsHistoryOpen(false);
          }}
          className="langflow-history-start-chat-btn"
        >
          {t('chat:chat_history.start_new_chat')}
        </Button>
      </div>
    </div>
  );
};

export default LangflowHistory;
